import type { ReactElement, ChangeEvent } from 'react';
import {
  memo,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActionIcon,
  Button,
  Group,
  Stack,
  Loader,
  Tooltip,
} from '@mantine/core';
import { IconMapPin, IconMapPinOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '@/store';
import { LazyTiptapEditor } from '@/components/notes/editor';
import { SlidePanel } from '@/components/ui/SlidePanel';
import type { SlidePanelHandle } from '@/components/ui/SlidePanel';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { AppTextInput } from '@/components/common/AppTextInput';
import { SwatchColorPicker } from '@/components/common/SwatchColorPicker';
import { InputLabel } from '@/components/common/InputLabel';
import { AutoSaveIndicator } from '@/components/common/AutoSaveIndicator';
import { RoadDisplayField } from '@/components/common/RoadDisplayField';
import { NOTE_COLOR_SWATCHES, NOTE_TITLE_MAX_LENGTH } from '@/constants/notes';
import { FormPanelTitle } from '@/components/common/form';
import { useGPSLocationSuggestion } from '@/hooks/useGPSLocationSuggestion';
import { useSlidePanelVariant } from '@/hooks';
import { useTaskSearch } from '@/hooks/useTaskSearch';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { NoteVisibility } from '@/types/notes';
import { useNoteForm } from './useNoteForm';
import { useNoteRoadDetection } from './useNoteRoadDetection';
import { VisibilityPills, TaskSearchSelect } from './components';
import styles from './NoteFormPanel.module.css';

const LazyNoteMiniMap = lazy(() =>
  import('@/components/map/NoteMiniMap').then((module) => ({
    default: module.NoteMiniMap,
  }))
);

const LOCATION_ICON_SIZE = 16;

function NoteFormPanelContent(): ReactElement {
  const { t } = useTranslation();
  const slidePanelVariant = useSlidePanelVariant();
  const [isFullscreenMode, setIsFullscreenMode] = useState(false);
  const { isMobile, width } = useDeviceLayout();
  const showTwoColumn = isFullscreenMode && !isMobile && width >= 1024;
  const slidePanelRef = useRef<SlidePanelHandle>(null);
  const wasFullscreenBeforePin = useRef(false);
  const setMobilePanelMinimized = useGeoMarkupStore(
    (state) => state.ui.setMobilePanelMinimized
  );

  const {
    formState,
    isOpen,
    isCreateMode,
    isSyncing,
    setTitle,
    setContent,
    setColor,
    setVisibility,
    setLocation,
    setLinkedTaskId,
    setCoordinates,
    handleSave,
    handleClose,
    handlePinToMap,
    handleRemoveAnchorPoint,
    handleImageUpload,
    markAsEdited,
    autoSaveStatus,
    hasLocation,
  } = useNoteForm();

  const {
    tasks: searchTasks,
    isLoading: isTaskSearchLoading,
    onSearchChange: handleTaskSearchChange,
  } = useTaskSearch(formState.linkedTaskId);

  const gpsLocationSuggestion = useGPSLocationSuggestion();

  const { roadSource, handleRequestGPSDetection, handleManualRoadInput } =
    useNoteRoadDetection({
      gpsLocationSuggestion,
      setLocation,
    });

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      markAsEdited();
      setTitle(event.currentTarget.value);
    },
    [setTitle, markAsEdited]
  );

  const handleContentChange = useCallback(
    (value: string) => {
      markAsEdited();
      setContent(value);
    },
    [setContent, markAsEdited]
  );

  const handlePinToMapWithFullscreen = useCallback(() => {
    if (isFullscreenMode) {
      wasFullscreenBeforePin.current = true;
      slidePanelRef.current?.exitFullscreen();
    }
    handlePinToMap();
  }, [isFullscreenMode, handlePinToMap]);

  // Restore fullscreen after map click sets location
  useEffect(() => {
    if (hasLocation && wasFullscreenBeforePin.current) {
      wasFullscreenBeforePin.current = false;
      slidePanelRef.current?.enterFullscreen();
    }
  }, [hasLocation]);

  const visibilityDescription =
    formState.visibility === NoteVisibility.Private
      ? t('notes.visibility.privateDescription')
      : t('notes.visibility.workspaceDescription');

  const headerTitle = isCreateMode
    ? t('notes.form.title.create')
    : t('notes.form.title.edit');

  const headerContent = (
    <FormPanelTitle indicator={<AutoSaveIndicator status={autoSaveStatus} />}>
      {headerTitle}
    </FormPanelTitle>
  );

  const footerContent = (
    <Group justify="space-between" w="100%">
      <Button variant="subtle" onClick={handleClose} disabled={isSyncing}>
        {t('common.cancel')}
      </Button>
      <Button
        onClick={handleSave}
        disabled={!formState.title.trim()}
        loading={isSyncing}
      >
        {t('notes.form.save')}
      </Button>
    </Group>
  );

  const canSave = !!formState.title.trim() && !isSyncing;
  const collapsedActions = useMemo(
    () => ({
      onCancel: handleClose,
      onSave: handleSave,
      onExpand: () => setMobilePanelMinimized(false),
      canSave,
      isSubmitting: isSyncing,
      saveLabel: t('notes.form.save'),
    }),
    [handleClose, handleSave, setMobilePanelMinimized, canSave, isSyncing, t]
  );

  const titleField = (
    <AppTextInput
      label={t('notes.form.titleLabel')}
      placeholder={t('notes.form.titlePlaceholder')}
      value={formState.title}
      onChange={handleTitleChange}
      maxLength={NOTE_TITLE_MAX_LENGTH}
      required
    />
  );

  const colorField = (
    <div className={styles.colorSection}>
      <InputLabel
        label={t('notes.form.colorLabel')}
        showCounter={false}
        currentLength={0}
      />
      <SwatchColorPicker
        value={formState.color}
        onChange={setColor}
        swatches={NOTE_COLOR_SWATCHES}
      />
    </div>
  );

  const visibilityField = (
    <div className={styles.visibilitySection}>
      <InputLabel
        label={t('notes.form.visibilityLabel')}
        showCounter={false}
        currentLength={0}
      />
      <VisibilityPills value={formState.visibility} onChange={setVisibility} />
      <span className={styles.visibilityDescription}>
        {visibilityDescription}
      </span>
    </div>
  );

  const editorField = (
    <div
      className={
        showTwoColumn ? styles.editorFieldExpanded : styles.editorField
      }
    >
      <InputLabel
        label={t('notes.form.contentLabel')}
        showCounter={false}
        currentLength={0}
      />
      <Suspense
        fallback={
          <div className={styles.editorFallback}>
            <Loader size="sm" />
          </div>
        }
      >
        <LazyTiptapEditor
          content={formState.content}
          onChange={handleContentChange}
          editable
          placeholder={t('notes.form.contentPlaceholder')}
          onImageUpload={handleImageUpload}
          variant={showTwoColumn ? 'expanded' : 'compact'}
        />
      </Suspense>
    </div>
  );

  const locationField = hasLocation ? (
    <Group gap="xs" align="flex-end" wrap="nowrap">
      <div className={styles.locationField}>
        <RoadDisplayField
          value={formState.location}
          onChange={setLocation}
          isDetectingGPS={gpsLocationSuggestion.isDetecting}
          gpsError={gpsLocationSuggestion.error}
          roadSource={roadSource}
          onRequestGPSDetection={handleRequestGPSDetection}
          onManualRoadInput={handleManualRoadInput}
        />
      </div>
      <Tooltip label={t('notes.form.removeFromMap')}>
        <ActionIcon
          variant="subtle"
          size="lg"
          onClick={handleRemoveAnchorPoint}
          aria-label={t('notes.form.removeFromMap')}
        >
          <IconMapPinOff size={LOCATION_ICON_SIZE} />
        </ActionIcon>
      </Tooltip>
    </Group>
  ) : (
    <Button
      variant="subtle"
      size="compact-sm"
      leftSection={<IconMapPin size={LOCATION_ICON_SIZE} />}
      onClick={handlePinToMapWithFullscreen}
      className={styles.pinToMapButton}
    >
      {t('notes.form.pinToMap')}
    </Button>
  );

  const taskField = (
    <TaskSearchSelect
      value={formState.linkedTaskId}
      onChange={setLinkedTaskId}
      tasks={searchTasks}
      onSearchChange={handleTaskSearchChange}
      isLoading={isTaskSearchLoading}
    />
  );

  return (
    <SlidePanel
      ref={slidePanelRef}
      opened={isOpen}
      onClose={handleClose}
      variant={slidePanelVariant}
      headerTitle={headerContent}
      footerContent={showTwoColumn ? undefined : footerContent}
      collapsedActions={collapsedActions}
      onFullscreenChange={setIsFullscreenMode}
      fullscreenWideContent={showTwoColumn}
      defaultFullscreen={false}
    >
      {showTwoColumn ? (
        <div className={styles.fullscreenWrapper}>
          <div className={styles.fullscreenLayout}>
            <AppScrollArea fill>
              <div className={styles.contentColumn}>
                {titleField}
                {editorField}
              </div>
            </AppScrollArea>
            <aside className={styles.metadataSidebar}>
              {colorField}
              {visibilityField}
              {hasLocation &&
                formState.latitude !== null &&
                formState.longitude !== null && (
                  <div className={styles.miniMapContainer}>
                    <Suspense fallback={null}>
                      <LazyNoteMiniMap
                        latitude={formState.latitude}
                        longitude={formState.longitude}
                        color={formState.color}
                        mode="edit"
                        onCoordinatesChange={setCoordinates}
                      />
                    </Suspense>
                  </div>
                )}
              {locationField}
              {taskField}
            </aside>
          </div>
          <div className={styles.formFooter}>{footerContent}</div>
        </div>
      ) : (
        <Stack gap="md">
          {titleField}
          {colorField}
          {visibilityField}
          {editorField}
          {locationField}
          {taskField}
        </Stack>
      )}
    </SlidePanel>
  );
}

export const NoteFormPanel = memo(NoteFormPanelContent);
